import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PullToRefresh from './components/PullToRefresh';
import { getApiUrl } from './config';
import { registerViaInvitation } from './utils/apiContentSharing';

function Register() {
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email');
  const tokenFromUrl = searchParams.get('token');
  const shareTokenFromUrl = searchParams.get('share_invitation_token');
  const isShareFlow = !!shareTokenFromUrl;

  const [formData, setFormData] = useState({
    username: '',
    email: emailFromUrl || '',
    password: '',
    confirmPassword: '',
    invitation_token: tokenFromUrl || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (formData.password.length > 72) {
      return 'Password must be at most 72 characters long';
    }
    if (formData.username.length < 3 || formData.username.length > 50) {
      return 'Username must be between 3 and 50 characters';
    }
    return null;
  };

  const extractRegisterErrorMessage = (err) => {
    if (err.response) {
      const detail = err.response.data.detail;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) return detail.map(d => d.msg).join(', ');
      return err.response.data.message || 'Registration failed. Please try again.';
    }
    if (err.request) {
      return 'Network error: Could not connect to server';
    }
    return 'An unexpected error occurred';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      if (isShareFlow) {
        await registerViaInvitation(
          formData.username,
          formData.email,
          formData.password,
          shareTokenFromUrl,
        );
      } else {
        await axios.post(
          getApiUrl('auth/register'),
          {
            username: formData.username,
            email: formData.email,
            password: formData.password,
            invitation_token: formData.invitation_token,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
      }

      setSuccess('Registration successful! Redirecting to login...');
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        invitation_token: '',
      });

      // After share-flow registration, send the user back to the invitation
      // page so they can sign in and accept the share. After regular
      // registration, send them to plain /login.
      const target = isShareFlow
        ? `/login?next=${encodeURIComponent(`/invitations/${shareTokenFromUrl}`)}`
        : '/login';
      setTimeout(() => {
        window.location.href = target;
      }, 2000);
    } catch (err) {
      setError(extractRegisterErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    window.location.reload();
  }, []);

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark px-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg p-8 shadow-lg">
            <div className="mb-6 text-center">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Join Dissona
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isShareFlow
                  ? 'Create your account to access the content shared with you.'
                  : 'Enter your email and invitation token to join Dissona'}
              </p>
            </div>

            {/* Share invitation banner */}
            {isShareFlow && (
              <div className="mb-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                <p className="text-sm text-gray-800 dark:text-gray-200">
                  You've been invited to view shared content on Dissona. Complete the form below
                  and we'll take you back to accept the share.
                </p>
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200">
                  {success}
                </p>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-200">
                  {error}
                </p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  minLength={3}
                  maxLength={50}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Choose a username"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  readOnly={!!emailFromUrl}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed read-only:bg-gray-50 read-only:dark:bg-gray-900 read-only:cursor-not-allowed"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  maxLength={72}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter password"
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Confirm password"
                />
              </div>

              {!isShareFlow && (
                <div>
                  <label
                    htmlFor="invitation_token"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Invitation Token
                  </label>
                  <input
                    type="text"
                    id="invitation_token"
                    name="invitation_token"
                    value={formData.invitation_token}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    readOnly={!!tokenFromUrl}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed read-only:bg-gray-50 read-only:dark:bg-gray-900 read-only:cursor-not-allowed"
                    placeholder="Enter your invitation token"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </PullToRefresh>
  );
}

export default Register;

